import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { MantineProvider, ColorSchemeScript } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { ClerkProvider } from '@clerk/clerk-react'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dates/styles.css'
import './index.css'
import App from './App.tsx'
import { mantineTheme } from './theme/theme'
import { mantineCssVariableResolver } from './theme/cssVariableResolver'
import { ApiProvider } from './components/providers'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {

        console.log('SW registered: ', registration)
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError)
      })
  })
}

// Import your Clerk Publishable Key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key")
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ColorSchemeScript defaultColorScheme="auto" />
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={{
        baseTheme: undefined, // Let CSS handle the theming
        variables: {
          colorPrimary: '#e60000',
          borderRadius: '8px',
          fontFamily: 'Inter, system-ui, sans-serif',
        },
      }}
    >
      <ApiProvider>
        <QueryClientProvider client={queryClient}>
          <MantineProvider
            theme={mantineTheme}
            cssVariablesResolver={mantineCssVariableResolver}
            defaultColorScheme="auto"
          >
            <Notifications />
            <App />
            <ReactQueryDevtools initialIsOpen={false} />
          </MantineProvider>
        </QueryClientProvider>
      </ApiProvider>
    </ClerkProvider>
  </StrictMode>,
)
